
import type { CurationTool, CurationToolRuleType } from '@empathyco/playboard-core-domain'
import type { PropType } from 'vue'
import type { PivotableAudit } from '../../../../composables/use-pivotable-audit'
import type {
  AuditItem,
  AuditMessages,
  ConfigurationsMessages,
  CurationToolMessages,
} from '../../../../models'
import type { AuditItemDetailsMessages } from './audit-item-details.component.vue'
import { AdapterBooleanUtils } from '@empathyco/playboard-core-domain'
import { computed, defineComponent, getCurrentInstance } from 'vue'
import { usePivotableAudit } from '../../../../composables/use-pivotable-audit'
import AuditItemDetails from './audit-item-details.component.vue'

export type AuditCurationTool = Pick<CurationTool, 'id' | 'metadata'> & {
  criteria: { query: string; enabled: string; filters: PivotableAudit['pivots'] }
} & {
  data: {
    rules: {
      added: Array<string>
      removed: Array<string>
    }
  }
}

export default defineComponent({
  name: 'CurationToolAuditItemDetails',
  components: {
    AuditItemDetails,
  },
  props: {
    item: {
      type: Object as PropType<AuditItem<CurationTool>>,
      required: true,
    },
    messages: {
      type: Object as PropType<AuditMessages['entry']>,
      required: true,
    },
  },
  setup(props) {
    const {
      proxy: { $epb },
    } = getCurrentInstance()

    const { transform: transformPivots } = usePivotableAudit()

    function filterRulesByType(
      rules: CurationTool['data']['rules'],
      type: CurationToolRuleType,
    ): CurationTool['data']['rules'] {
      return rules.reduce((result, rule) => {
        if (rule.type === type) {
          result.push(rule)
        }
        return result
      }, [])
    }

    const removeAllKey = (object: Record<string, any>) => {
      if ('all' in object) {
        delete object.all
      }
      return object
    }

    function getPivotsData(criteria: CurationTool['criteria']): PivotableAudit['pivots'] {
      const pivots = transformPivots(criteria).pivots
      return removeAllKey(pivots)
    }

    function transformState(state: CurationTool): AuditCurationTool {
      const { id, metadata, criteria, data } = state

      const rules = {
        added: filterRulesByType(data.rules, 'ADD').map(rule => rule.query),
        removed: filterRulesByType(data.rules, 'REMOVE').map(rule => rule.query),
      }
      const toolMessages: ConfigurationsMessages = $epb.messages.tools.configurations
      return {
        id,
        metadata,
        criteria: {
          enabled: AdapterBooleanUtils.toBoolean(state.criteria.enabled)
            ? toolMessages.fields.enabled.states.enabled
            : toolMessages.fields.enabled.states.disabled,
          query: criteria.queries[0],
          filters: getPivotsData(criteria),
        },
        data: {
          rules,
        },
      }
    }
    const transformedItem = computed<AuditItem<AuditCurationTool>>(() => {
      const { previousState, nextState } = props.item.payload
      return {
        ...props.item,
        payload: {
          ...props.item.payload,
          previousState: previousState ? transformState(previousState) : undefined,
          nextState: nextState ? transformState(nextState) : undefined,
        },
      }
    })

    const transformedMessages = computed<AuditItemDetailsMessages>(() => {
      const toolMessages = $epb.messages.tools.nextQueries as CurationToolMessages
      return {
        previousState: props.messages.previousState,
        nextState: props.messages.nextState,
        fields: {
          created: toolMessages.fields.creationDate.label,
          updated: toolMessages.fields.updateDate.label,
          author: toolMessages.fields.user.label,
          enabled: toolMessages.fields.enabled.label,
          query: toolMessages.fields.query.label,
          rules: toolMessages.fields.rules.label,
          'rules.added': toolMessages.fields.rules.types.add,
          'rules.removed': toolMessages.fields.rules.types.remove,
        },
      }
    })

    return {
      transformedItem,
      transformedMessages,
    }
  },
})
